import { dataService, getIndexedId } from "@thegetty-private/rad-app-utils";
import {
  getDataUrl,
  getIdManagerDataUrl,
  getApiPath
} from "@/helpers/configHelper";
import { getName } from "@thegetty-private/linked-data.js";
import {
  transformSearchResultForCarousel,
  transformSearchResultForImageAndCaption
} from "@/helpers/Search";
import * as informationObjectHelpers from "@/helpers/InformationObject";
import * as aat from "@/data/aat";
import * as internal from "@/data/internal-ontologies";

let apiPath = getApiPath();
let dataUrl = getDataUrl("/");
let idManagerDataUrl = getIdManagerDataUrl();

/**
 * allow the overwriting of the api path (testing)
 *
 * @param {string} path
 */
export function setDataUrl(url) {
  dataUrl = url;
}

/**
 * allow the overwriting of the api path (testing)
 *
 * @param {string} path
 */
export function setApiPath(url) {
  apiPath = url;
}

/**
 * query Elasticsearch for collections related to the actor
 *
 * @param {string} id
 *
 * @returns {object} an object containing the params sent to ES, the totals and the results of the query
 */
export async function getRelatedCollectionsForPerson(id) {
  let params = {
    from: 0,
    size: 23,
    filters: { rel_ids: [id], entity_type: ["collection"] },
    query: [],
    root: apiPath,
    sort: "primary_name"
  };
  try {
    let relatedCollections = await dataService.paramsSearch(params);
    return transformSearchResults(relatedCollections, params);
  } catch (err) {
    console.error(err);
  }
}

/**
 * transforms the results into a consistent form and transforms each item
 *
 * @param {object} results
 * @param {object} params
 * @param {function} transformer
 *
 * @returns {object} an object containing the params sent to ES, the totals and the results of the query
 */
function transformSearchResults(
  results,
  params,
  transformer = transformSearchResultForCarousel
) {
  if (results && results.data) {
    return {
      params: params,
      total: results.data.total,
      result: results.data.data.map((item) => transformer(item))
    };
  }
}

/**
 * query Elasticsearch for collections related to the actor
 *
 * @param {string} id
 *
 * @returns {object} an object containing the params sent to ES, the totals and the results of the query
 */
export async function getRelatedCollectionsForCollection(id) {
  let params = {
    from: 0,
    size: 23,
    filters: { id: [id] },
    query: [],
    root: apiPath,
    path: "api/relatedCollections",
    sort: "primary_name"
  };
  try {
    let relatedCollections = await dataService.paramsSearch(params);
    return transformSearchResults(relatedCollections, params);
  } catch (err) {
    console.error(err);
  }
}

/**
 * Adds the related items for the collection
 *
 * @param {string} indexedId - the IndexedId
 * @param {object} ref - the reference to the store
 * @async
 */
export async function addRelatedItems(indexedId, ref) {
  let relatedItems = await getRelatedItems({
    part_of: [indexedId],
    type: ["HumanMadeObject"]
  });

  if (relatedItems?.result?.length) {
    relatedItems.result = relatedItems.result.map((item) =>
      transformSearchResultForImageAndCaption(item)
    );
  }

  ref.relatedItems = relatedItems;
}

/**
 * query Elasticsearch for items related to the actor
 *
 * @param {object} filters an object containing filters to pass to the ES query via /search
 *
 * @returns {object} an object containing the params sent to ES, the totals and the results of the query
 */
export async function getRelatedItems(filters) {
  let params = {
    from: 0,
    size: 23,
    filters: filters,
    query: [],
    root: apiPath,
    sort: "tree_order"
  };
  try {
    let relatedItems = await dataService.paramsSearch(params);
    return transformSearchResults(relatedItems, params);
  } catch (err) {
    console.error(err);
  }
}

/**
 * query Elasticsearch for items related to the actor
 *
 * @param {object} filters an object containing filters to pass to the ES query via /relatedActors
 *
 * @returns {object} an object containing the params sent to ES, the totals and the results of the query
 */
export async function getRelatedActors(filters, path = "api/relatedActors") {
  let params = {
    filters: filters,
    root: apiPath,
    path: path,
    // following params are not consumed by default relatedActors endpoint
    query: [],
    from: 0,
    size: 23
  };

  try {
    let relatedActors = await dataService.paramsSearch(params);
    return transformSearchResults(relatedActors, params);
  } catch (err) {
    console.error(err);
  }
}

/**
 * Gets the ElasticSearch result for an ID
 *
 * @param {string} id - the indexed ID
 * @param {string} root - the base of the API
 *
 * @returns
 */
export async function getElasticObject(id, root = apiPath) {
  const url = `${root}api/get?id=${id}`;
  try {
    let result = await dataService.axiosGet(url);
    return result?.data?.data[0];
  } catch (e) {
    console.error(e);
  }
  return {};
}

/**
 * Flattens the agent information into a unique map of agents & roles
 *
 * @param {object} agent - the agent
 * @param {object} map - the map of all agents
 * @param {object} extraRole - the extra role
 *
 */
function flattenAgent(agent, map, extraRole) {
  const cid = getIndexedId(agent.id);
  const existing = map[cid];
  if (Array.isArray(agent.assigned) == false) {
    agent.assigned = [];
  }
  if (extraRole) {
    agent.assigned.push(extraRole);
  }
  if (existing) {
    existing.assigned = existing.assigned.concat(agent.assigned);
  } else {
    map[cid] = agent;
  }
}

/**
 * Adds the tree information to the reference object
 *
 * @param {object} ref - the reference to the store object
 * @param {string} indexedId - the indexed id
 */
export async function addItemTreeInfo(ref, indexedId) {
  const url = apiPath + "api/itemtree";
  const searchParams = new URLSearchParams({
    id: indexedId,
    type: "ArchivalPart"
  });
  try {
    const results = await dataService.axiosGet(url, undefined, searchParams);
    ref.treeData = results?.data;
  } catch (e) {
    console.error(e);
  }
}

/**
 * Applies a route to a stub
 *
 * @param {object} stub - the stub object with an id
 * @param {object} slugs - the map of sluts
 *
 */
export function applyRouteToStub(stub, slugs) {
  if (stub?.id == undefined) {
    return;
  }
  const id = getIndexedId(stub.id);
  const entry = slugs[id];
  if (entry?.slug) {
    stub.to = {
      name: entry.path,
      params: {
        slug: entry.slug
      },
      state: {
        slug: entry.slug,
        id: id
      }
    };
  }
}

/**
 * Creates a stub object from an ElasticSearch Object
 *
 * @param {object} obj - the object
 *
 * @returns {object}
 */
function createStubFromElastic(obj) {
  if (!obj) {
    return;
  }
  // should this be markdown
  return {
    name: obj.primary_name,
    id: getIndexedId(obj.id)
  };
}

/**
 * Gets the Subjects, Agents, and Roles from the raw data
 *
 * @param {object} lodObject - the JSON-LD object
 *
 * @returns {object}
 */
export async function getSubjectsAndAgents(lodObject) {
  try {
    let map = {};
    [
      ...informationObjectHelpers.getCreatorsWithRoles(lodObject),
      ...informationObjectHelpers.getMetadataActors(lodObject)
    ].forEach((c) => {
      flattenAgent(c, map);
    });

    informationObjectHelpers.getDepictedActors(lodObject).forEach((c) => {
      flattenAgent(c, map, { id: internal.DEPICTED, type: "Type" });
    });

    informationObjectHelpers.getAboutActors(lodObject).forEach((c) => {
      flattenAgent(c, map, { id: "subject", type: "Type" });
    });
    const actorStubs = Object.values(map);

    let agents = [];
    try {
      agents = await dataService.getAllById(
        actorStubs.map((a) => a.id),
        dataUrl
      );
    } catch (e) {
      console.error(e);
    }

    const subjectStubs =
      informationObjectHelpers.getSubjectReferences(lodObject);
    let subjects = [];
    try {
      subjects = await dataService.getAllById(
        subjectStubs.map((a) => a.id),
        dataUrl
      );
    } catch (e) {
      console.error(e);
    }

    let roleMap = {};
    actorStubs.forEach((stub) => {
      roleMap[getIndexedId(stub.id)] = stub.assigned.map((role) => role.id);
    });

    agents = agents.map((actor) => {
      let aid = getIndexedId(actor.id);
      return {
        name: getName(actor),
        type: actor.type,
        id: aid,
        relators: roleMap[aid]
      };
    });

    let primaryCreators = [];
    let otherCreators = [];
    let aboutActors = agents.filter((a) => a.relators.includes("about"));
    agents.forEach((a) => {
      if (a.relators.includes(aat.PREFERRED_TERMS)) {
        primaryCreators.push(a);
      } else {
        otherCreators.push(a);
      }
    });

    return { agents, subjects, primaryCreators, aboutActors, otherCreators };
  } catch (e) {
    console.error(e);
  }
  return {};
}
/**
 * Adds the ElasticSearch data (next/prev/collection) to the Store Object
 *
 * @param {object} ref - the store object
 * @param {string} indexedId - the Indexed Id
 */
export async function addElasticSearchInfo(ref, indexedId) {
  try {
    let result = await getElasticObject(indexedId, apiPath);
    if (!result) {
      return;
    }
    if (result.next) {
      ref.next = createStubFromElastic(result.next);
    }
    if (result.prev) {
      ref.prev = createStubFromElastic(result.prev);
    }
    if (result.parent) {
      ref.parent = createStubFromElastic({ id: result.parent });
    }
    if (result.part_of_collection) {
      ref.collection = createStubFromElastic(result.part_of_collection);
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Adds the Collection Metadata information to the store object
 *
 * @param {object} ref - the store object
 * @param {string} collectionL0d - the collection LOD Object
 * @param {function} translate - the translate function
 * @param {boolean} isHmo - setup for HMO
 */
export async function addCollectionMetadata(
  ref,
  collectionId,
  translate,
  isHmo
) {
  if (collectionId == undefined) {
    return;
  }
  try {
    let collectionLod = await dataService.getAllById(collectionId, dataUrl);

    if (collectionLod == undefined) {
      return;
    }

    ref.collectionMetadata = {
      ...informationObjectHelpers.getCollectionMetadataForArchivalPart(
        collectionLod,
        [
          {
            name: informationObjectHelpers.getPageTitle(
              collectionLod,
              false,
              translate
            ),
            id: getIndexedId(collectionId)
          }
        ],
        isHmo
      ),

      publication_rights: informationObjectHelpers.getPublicationRights(
        collectionLod,
        translate
      )
    };

    ref.collection.catalogLink =
      informationObjectHelpers.getCatalogRecord(collectionLod);
    return collectionLod;
  } catch (e) {
    console.error(e);
  }
}

/**
 * Applies slugs to the entries in the store object
 *
 * @param {object} ref - the reference to the store object
 * @param {array} agents - a list of agents
 */
export async function applySlugs(ref, agents = []) {
  const sharedRefs = [
    ref?.collectionMetadata?.collection_name[0],
    ref.next,
    ref.parent,
    ref.prev,
    ...(ref.treeData?.parents || []),
    ...(ref.treeData?.children || [])
  ];

  const objs = [...agents, ...sharedRefs].filter((a) => a != null);
  if (objs.length == 0) {
    return;
  }

  let slugs = {};
  let stubs = [...sharedRefs];
  try {
    slugs = await dataService.getSlugs(
      objs,
      idManagerDataUrl,
      internal.ID_MANAGER_SOURCE_GENERATOR,
      internal.ID_MANAGER_TARGET_GENERATOR
    );

    if (ref.metadata) {
      Object.entries(ref.metadata).forEach(([key, val]) => {
        if (
          val &&
          (["primary_creator", "primary_creators", "subjects_names"].includes(
            key
          ) ||
            key.includes("relators"))
        ) {
          stubs = stubs.concat(val);
        }
      });
    }
  } catch (e) {
    console.error(e);
  }
  stubs.forEach((stub) => applyRouteToStub(stub, slugs));
}
